// Client-portal billing API wrappers. Mirrors api/billingThreshold.js
// but talks to the client-scoped endpoints (no staff-only actions).
import api from '@src/api/clients';

const BILLING_BASE = 'dashboard/my-billing-profiles/';
const LEDGER_BASE = 'dashboard/my-threshold-charges/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

export const listMyBillingProfiles = () =>
  api.request({ url: BILLING_BASE, method: 'GET' });

export const retrieveMyBillingProfile = (profileId) =>
  api.request({ url: `${BILLING_BASE}${profileId}/`, method: 'GET' });

export const fetchMyBillingLiveStatus = (profileId) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/live-status/`,
    method: 'GET',
  });

export const fetchMyPaymentMethods = (profileId) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/payment-methods/`,
    method: 'GET',
  });

export const createMySetupIntent = (profileId, paymentMethodType = 'card') =>
  api.request({
    url: `${BILLING_BASE}${profileId}/create-setup-intent/`,
    method: 'POST',
    payload: { payment_method_type: paymentMethodType },
  });

export const setMyDefaultPaymentMethod = (profileId, paymentMethodId) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/set-default-payment-method/`,
    method: 'POST',
    payload: { payment_method_id: paymentMethodId },
  });

export const detachMyPaymentMethod = (profileId, paymentMethodId) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/detach-payment-method/`,
    method: 'POST',
    payload: { payment_method_id: paymentMethodId },
  });

export const fetchMyPrepaidTerms = (profileId) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/prepaid-terms/`,
    method: 'GET',
  });

export const enableMyPrepaid = (profileId, { termsVersion } = {}) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/enable-prepaid/`,
    method: 'POST',
    payload: { terms_version: termsVersion },
  });

export const registerMyPendingBankVerification = (profileId, payload) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/register-pending-verification/`,
    method: 'POST',
    payload,
  });

export const updateMyThreshold = (profileId, thresholdAmount) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/update-threshold/`,
    method: 'POST',
    payload: { threshold_amount: thresholdAmount },
  });

export const updateMyPaymentPriority = (profileId, priority) =>
  api.request({
    url: `${BILLING_BASE}${profileId}/update-payment-priority/`,
    method: 'POST',
    payload: { payment_method_priority: priority },
  });

export const listMyThresholdCharges = (params = {}) =>
  api.request({
    url: LEDGER_BASE,
    method: 'GET',
    payload: cleanParams(params),
  });

// Monthly spend statements (read-only; staff + client, server-scoped).
const STATEMENT_BASE = 'dashboard/my-statements/';

export const listStatementOptions = () =>
  api.request({ url: STATEMENT_BASE, method: 'GET' });

export const fetchStatementData = (params = {}) =>
  api.request({
    url: `${STATEMENT_BASE}data/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const downloadStatementPdf = (params = {}) =>
  api
    .request({
      url: `${STATEMENT_BASE}pdf/`,
      method: 'GET',
      payload: cleanParams(params),
      options: { responseType: 'blob' },
    })
    .then((resp) => resp?.data ?? resp);

export const downloadStatementCsv = (params = {}) =>
  api
    .request({
      url: `${STATEMENT_BASE}csv/`,
      method: 'GET',
      payload: cleanParams(params),
      options: { responseType: 'blob' },
    })
    .then((resp) => resp?.data ?? resp);
